interface ErrorPattern {
  source: 'network' | 'extension' | 'resource';
  pattern: RegExp | string;
  description: string;
}

class ErrorSuppressor {
  private isProduction: boolean;
  private errorHandler: ((event: ErrorEvent) => void) | null = null;
  private rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
  private originalConsoleError: typeof console.error | null = null;
  private originalConsoleWarn: typeof console.warn | null = null;
  private suppressedPatterns: ErrorPattern[] = [
    // Slug API 404s - these are expected when slugs don't exist yet
    {
      source: 'network',
      pattern: /\/slugs\/(product-slug|category-slug)\//,
      description: 'Slug API 404 errors'
    },
    // Browser extension messages
    {
      source: 'extension',
      pattern: /Nm(OfflineStatus|LockState)/,
      description: 'Browser extension messages'
    },
    // Missing manifest resources
    {
      source: 'resource',
      pattern: /logo192\.png/,
      description: 'Missing manifest icon'
    },
    // Common extension patterns
    {
      source: 'extension',
      pattern: /native (core|messaging)/i,
      description: 'Native messaging from extensions'
    },
    // MyPOS SDK loading messages (informational only)
    {
      source: 'resource',
      pattern: /MyPOS SDK not loaded/i,
      description: 'MyPOS SDK loading status messages'
    },
    // MyPOS payment container not found (timing issue during initialization)
    {
      source: 'resource',
      pattern: /mypos-payment-container.*not found/i,
      description: 'MyPOS payment container initialization messages'
    }
  ];

  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';

    if (this.isProduction) {
      this.setupErrorInterceptors();
    }
  }

  private setupErrorInterceptors(): void {
    // Store original console methods
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;

    // Override console.error to filter out known patterns
    console.error = (...args: any[]) => {
      if (this.shouldSuppressError(args)) {
        return;
      }
      this.originalConsoleError?.apply(console, args);
    };

    // Override console.warn to filter out known patterns
    console.warn = (...args: any[]) => {
      if (this.shouldSuppressError(args)) {
        return;
      }
      this.originalConsoleWarn?.apply(console, args);
    };

    // Create bound handlers to maintain 'this' context and allow removal
    this.errorHandler = (event: ErrorEvent) => {
      if (this.shouldSuppressErrorEvent(event)) {
        event.preventDefault();
        return false;
      }
    };

    this.rejectionHandler = (event: PromiseRejectionEvent) => {
      if (this.shouldSuppressRejection(event)) {
        event.preventDefault();
        return false;
      }
    };

    // Add event listeners with stored references
    window.addEventListener('error', this.errorHandler, true);
    window.addEventListener('unhandledrejection', this.rejectionHandler, true);
  }

  private shouldSuppressError(args: any[]): boolean {
    const errorString = args.map(arg => {
      try {
        return typeof arg === 'object' ? JSON.stringify(arg) : String(arg);
      } catch (e) {
        // Fallback for circular references or other JSON errors
        return String(arg);
      }
    }).join(' ');

    return this.suppressedPatterns.some(pattern => {
      if (typeof pattern.pattern === 'string') {
        return errorString.includes(pattern.pattern);
      }
      return pattern.pattern.test(errorString);
    });
  }

  private shouldSuppressErrorEvent(event: ErrorEvent): boolean {
    const { message, filename } = event;

    // Check if it's a resource loading error
    if (filename && this.suppressedPatterns.some(p =>
      p.source === 'resource' && this.matchesPattern(filename, p.pattern)
    )) {
      return true;
    }

    // Check error message
    return this.suppressedPatterns.some(p =>
      this.matchesPattern(message, p.pattern)
    );
  }

  private shouldSuppressRejection(event: PromiseRejectionEvent): boolean {
    const reason = event.reason;

    // Check for network errors
    if (reason?.config?.url) {
      return this.suppressedPatterns.some(p =>
        p.source === 'network' && this.matchesPattern(reason.config.url, p.pattern)
      );
    }

    // Check error message
    const errorMessage = reason?.message || String(reason);
    return this.suppressedPatterns.some(p =>
      this.matchesPattern(errorMessage, p.pattern)
    );
  }

  private matchesPattern(text: string, pattern: RegExp | string): boolean {
    if (typeof pattern === 'string') {
      return text.includes(pattern);
    }
    return pattern.test(text);
  }

  // Method to add custom suppression patterns at runtime
  public addSuppressionPattern(pattern: ErrorPattern): void {
    this.suppressedPatterns.push(pattern);
  }

  // Method to check if suppression is active
  public isActive(): boolean {
    return this.isProduction;
  }

  // Method to get suppressed patterns (for debugging)
  public getPatterns(): ErrorPattern[] {
    return [...this.suppressedPatterns];
  }

  // Cleanup method to remove all event listeners and restore console methods
  public cleanup(): void {
    // Remove event listeners if they exist
    if (this.errorHandler) {
      window.removeEventListener('error', this.errorHandler, true);
      this.errorHandler = null;
    }

    if (this.rejectionHandler) {
      window.removeEventListener('unhandledrejection', this.rejectionHandler, true);
      this.rejectionHandler = null;
    }

    // Restore original console methods
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
      this.originalConsoleError = null;
    }

    if (this.originalConsoleWarn) {
      console.warn = this.originalConsoleWarn;
      this.originalConsoleWarn = null;
    }
  }
}

// Create and export singleton instance
const errorSuppressor = new ErrorSuppressor();
export default errorSuppressor;